import { useEffect, useMemo, useRef, useState } from "react";

interface TabMenuProps {
  tabs: string[];
  activeTab: string;
  setCurrentTab: (tab: string) => void;
  navigate: (path: string) => void;
  // Optional: id of the tabpanel element you render below; if you render one per tab, we derive it.
  tabPanelIdBase?: string; // e.g. "dashboard-panel"
}

type TabMeta = {
  id: string;           // id of the tab element
  panelId: string;      // id of the associated tabpanel
  name: string;
};

const keyCodes = {
  End: "End",
  Home: "Home",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  Enter: "Enter",   
  Space: " ",
};

const srOnly = "sr-only absolute -m-px h-px w-px overflow-hidden p-0 border-0";

const TabMenu: React.FC<TabMenuProps> = ({
  tabs,
  activeTab,
  setCurrentTab,
  navigate,
  tabPanelIdBase = "tabpanel",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dynamicTab, setDynamicTab] = useState<string | null>(null);
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(4);

  const tabRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const menuRef = useRef<HTMLDivElement>(null);
  const tablistRef = useRef<HTMLDivElement>(null);

  // ---- ID wiring for tabs & panels
  const tabMeta: TabMeta[] = useMemo(() => {
    return (tabs || []).map((t) => {
      const safe = t.toLowerCase().replace(/\s+/g, "-");
      return {
        name: t,
        id: `tab-${safe}`,
        panelId: `${tabPanelIdBase}-${safe}`,
      };
    });
  }, [tabs, tabPanelIdBase]);

  const indexOf = (name: string) =>
    tabMeta.findIndex((t) => t.name.toLowerCase() === name?.toLowerCase());

  // ---- Responsive visible tabs
  const calculateMaxVisibleTabs = () => {
    const width = window.innerWidth;
    if (width > 464) return 5;
    if (width > 360) return 4;
    if (width > 300) return 3;
    if (width > 250) return 2;
    return 1;
  };

  useEffect(() => {
    const updateVisibleTabs = () => setMaxVisibleTabs(calculateMaxVisibleTabs());
    updateVisibleTabs();
    window.addEventListener("resize", updateVisibleTabs);
    return () => window.removeEventListener("resize", updateVisibleTabs);
  }, []);

  // Click-away for the overflow menu
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  if (!tabs || tabs.length === 0) return null;

  const activeLower = activeTab?.toLowerCase();

  const baseVisibleCount = dynamicTab ? maxVisibleTabs - 1 : maxVisibleTabs;
  const baseVisible = tabs.slice(0, Math.max(0, baseVisibleCount));

  const showOnlyActive =
    maxVisibleTabs <= 2 && activeTab && tabs.includes(activeTab);

  let visibleTabs: string[];
  if (showOnlyActive) {
    visibleTabs = [activeTab];
  } else {
    visibleTabs = dynamicTab
      ? [...baseVisible.filter((t) => t !== dynamicTab), dynamicTab]
      : baseVisible;
  }

  const visibleSet = new Set(visibleTabs);
  const extraTabs = tabs.filter((t) => !visibleSet.has(t));

  // aria-owns needs all tab IDs, including overflowed
  const allTabIds = tabMeta.map((t) => t.id).join(" ");

  // ---- behavior helpers
  const activateTab = (name: string, fromHamburger = false) => {
    if (fromHamburger) setDynamicTab(name);
    navigate(`/dashboard/${name}`);
    setCurrentTab(name);
    setMenuOpen(false);
    // Move DOM focus to the activated tab after state changes
    requestAnimationFrame(() => {
      const el = tabRefs.current.get(name);
      el?.focus();
    });
  };

  const focusByIndex = (idx: number) => {
    const meta = tabMeta[idx];
    if (!meta) return;
    const el = tabRefs.current.get(meta.name);
    el?.focus();
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const key = e.key;
    const currentName = (e.currentTarget.dataset.name as string) || activeTab;
    const currentIndex = indexOf(currentName);

    if (
      key === keyCodes.ArrowRight ||
      key === keyCodes.ArrowDown ||
      key === keyCodes.ArrowLeft ||
      key === keyCodes.ArrowUp ||
      key === keyCodes.Home ||
      key === keyCodes.End
    ) {
      e.preventDefault();
    }

    switch (key) {
      case keyCodes.ArrowRight:
      case keyCodes.ArrowDown: {
        const next = (currentIndex + 1) % tabMeta.length;
        // If the next tab is overflowed, open menu and focus it there
        const nextName = tabMeta[next].name;
        if (!visibleSet.has(nextName)) {
          setMenuOpen(true);
          requestAnimationFrame(() => {
            const el = tabRefs.current.get(nextName);
            el?.focus();
          });
        } else {
          focusByIndex(next);
        }
        break;
      }
      case keyCodes.ArrowLeft:
      case keyCodes.ArrowUp: {
        const prev = (currentIndex - 1 + tabMeta.length) % tabMeta.length;
        const prevName = tabMeta[prev].name;
        if (!visibleSet.has(prevName)) {
          setMenuOpen(true);
          requestAnimationFrame(() => {
            const el = tabRefs.current.get(prevName);
            el?.focus();
          });
        } else {
          focusByIndex(prev);
        }
        break;
      }
      case keyCodes.Home: {
        if (!visibleSet.has(tabMeta[0].name)) setMenuOpen(true);
        focusByIndex(0);
        break;
      }
      case keyCodes.End: {
        const last = tabMeta.length - 1;
        if (!visibleSet.has(tabMeta[last].name)) setMenuOpen(true);
        focusByIndex(last);
        break;
      }
      case keyCodes.Enter:
      case keyCodes.Space: {
        activateTab(currentName);
        break;
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-1 py-2 no-print bg-white relative">
      {/* The actual tablist: expose ALL tabs via aria-owns, even those rendered in the overflow. */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Sections"
        aria-owns={allTabIds}
        className="flex items-center gap-1"
      >
        {/* SR helper: count of tabs */}
        <span className={srOnly} aria-live="off">
          {tabs.length} tabs
        </span>

        {visibleTabs.map((name) => {
          const meta = tabMeta[indexOf(name)];
          const isActive = activeLower === name.toLowerCase();
          return (
            <button
              key={name}
              id={meta.id}
              role="tab"
              data-name={name}
              aria-selected={isActive}
              aria-controls={meta.panelId}
              tabIndex={isActive ? 0 : -1}
              ref={(el) => tabRefs.current.set(name, el)}
              className={`px-3 py-2 text-[10px] font-semibold rounded-md border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green
                ${
                  isActive
                    ? "bg-port-green text-white border-port-green"
                    : "bg-[#21506F] text-white border-white hover:bg-port-green hover:text-white"
                }`}
              onClick={() => activateTab(name)}
              onKeyDown={onTabKeyDown}
            >
              {name}
            </button>
          );
        })}

        {/* Overflow (still tabs): the trigger is NOT a tab; it’s a menu button. */}
        {extraTabs.length > 0 && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label="More tabs"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="px-3 py-2 text-[10px] font-semibold rounded-md border bg-[#21506F] text-white hover:bg-port-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green"
            >
              ☰
            </button>

            {menuOpen && (
              <div
                role="menu"
                aria-label="Additional tabs"
                className="absolute right-0 mt-2 w-44 bg-white border rounded shadow-lg z-50 p-1"
              >
                {extraTabs.map((name) => {
                  const meta = tabMeta[indexOf(name)];
                  const isActive = activeLower === name.toLowerCase();
                  return (
                    <button
                      key={name}
                      id={meta.id}
                      role="tab"
                      data-name={name}
                      aria-selected={isActive}
                      aria-controls={meta.panelId}
                      tabIndex={isActive ? 0 : -1}
                      ref={(el) => tabRefs.current.set(name, el)}
                      onKeyDown={onTabKeyDown}
                      onClick={() => activateTab(name, true)}
                      className={`w-full text-left px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-port-green ${
                        isActive
                          ? "bg-port-green text-white"
                          : "hover:bg-gray-100 text-[#1f2937]"
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TabMenu;
